import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Eye, ArrowLeft } from 'lucide-react';

import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { StepZones } from '@/components/onboarding/StepZones';
import { StepTables } from '@/components/onboarding/StepTables';
import { StepTimeSlots } from '@/components/onboarding/StepTimeSlots';
import { StepBookingMode } from '@/components/onboarding/StepBookingMode';
import { StepNotifications } from '@/components/onboarding/StepNotifications';
import { StepComplete } from '@/components/onboarding/StepComplete';
import { SkipAllDialog } from '@/components/onboarding/SkipAllDialog';

const STEP_TITLES = [
  'Zones',
  'Tables',
  'Créneaux horaires',
  'Mode de réservation',
  'Notifications',
  'Terminé'
];

const PREVIEW_ESTABLISHMENT = {
  id: 'preview-establishment',
  name: 'Restaurant Démo',
  slug: 'demo',
  email: 'contact@demo.ch',
};

export default function AdminOnboardingPreview() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSkipAllDialog, setShowSkipAllDialog] = useState(false);

  const handleSkipAll = () => {
    setShowSkipAllDialog(true);
  };

  const handleConfirmSkipAll = () => {
    setCurrentStep(6); // Jump to final step
  };

  // Collected data (stored in memory only - not saved to database)
  const [zonesData, setZonesData] = useState<{
    zones: Array<{ name: string; color: string }>;
  } | null>(null);

  const [tablesData, setTablesData] = useState<{
    tables: Array<{ name: string; capacity: number; zoneName?: string }>;
  } | null>(null);
  
  const [timeSlotsData, setTimeSlotsData] = useState<{
    slots: Array<{ startTime: string; endTime: string; days: number[] }>;
  } | null>(null);

  const [bookingModeData, setBookingModeData] = useState<{
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  } | null>(null);
  
  const [notificationsData, setNotificationsData] = useState<{
    newReservation: boolean;
    dailySummary: boolean;
    sendTime: string;
    recipientEmail: string;
  } | null>(null);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleZonesComplete = (data: { zones: Array<{ name: string; color: string }> }) => {
    setZonesData(data);
    handleNext();
  };

  const handleTablesComplete = (data: { tables: Array<{ name: string; capacity: number; zoneName?: string }> }) => {
    setTablesData(data);
    handleNext();
  };

  const handleTimeSlotsComplete = (data: { slots: Array<{ startTime: string; endTime: string; days: number[] }> }) => {
    setTimeSlotsData(data);
    handleNext();
  };

  const handleBookingModeComplete = (data: {
    bookingMode: 'slot' | 'time';
    bookingIntervalMinutes: 15 | 30 | 60;
    reservationDurationMinutes: number;
  }) => {
    setBookingModeData(data);
    handleNext();
  };

  const handleNotificationsComplete = (data: {
    newReservation: boolean;
    dailySummary: boolean;
    sendTime: string;
    recipientEmail: string;
  }) => {
    setNotificationsData(data);
    handleNext();
  };

  // Preview mode: show confirmation toast instead of saving
  const handleFinish = () => {
    toast({
      title: "Aperçu terminé !",
      description: "En production, l'établissement serait maintenant configuré avec ces paramètres.",
    });
  };

  const handleQuitPreview = () => {
    navigate('/admin');
  };

  return (
    <div className="relative">
      <SkipAllDialog
        open={showSkipAllDialog}
        onOpenChange={setShowSkipAllDialog}
        onConfirmSkip={handleConfirmSkipAll}
      />
      {/* Preview Mode Banner */}
      <div className="sticky top-0 z-50 bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900">
              <Eye className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Mode aperçu</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Aucune donnée ne sera enregistrée
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleQuitPreview}
            className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Quitter l'aperçu
          </Button>
        </div>
      </div>

      {/* Onboarding Content */}
      <OnboardingLayout
        currentStep={currentStep}
        totalSteps={6}
        stepTitles={STEP_TITLES}
        establishmentName={PREVIEW_ESTABLISHMENT.name}
        onSkipAll={handleSkipAll}
        showSkipAll={true}
      >
        {currentStep === 1 && (
          <StepZones
            onComplete={handleZonesComplete}
            onSkip={handleSkip}
          />
        )}
        {currentStep === 2 && (
          <StepTables
            zones={zonesData?.zones || []}
            onComplete={handleTablesComplete}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        )}
        {currentStep === 3 && (
          <StepTimeSlots
            onComplete={handleTimeSlotsComplete}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        )}
        {currentStep === 4 && (
          <StepBookingMode
            onComplete={handleBookingModeComplete}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        )}
        {currentStep === 5 && (
          <StepNotifications
            defaultEmail={PREVIEW_ESTABLISHMENT.email}
            onComplete={handleNotificationsComplete}
            onBack={handleBack}
            onSkip={handleSkip}
          />
        )}
        {currentStep === 6 && (
          <StepComplete
            establishmentSlug={PREVIEW_ESTABLISHMENT.slug}
            zonesCount={zonesData?.zones.length || 0}
            tablesCount={tablesData?.tables.length || 0}
            timeSlotsCount={timeSlotsData?.slots.length || 0}
            notificationsEnabled={notificationsData?.newReservation || notificationsData?.dailySummary || false}
            bookingMode={bookingModeData?.bookingMode}
            bookingIntervalMinutes={bookingModeData?.bookingIntervalMinutes}
            reservationDurationMinutes={bookingModeData?.reservationDurationMinutes}
            onFinish={handleFinish}
            onGoToFloorPlan={handleFinish}
            onBack={handleBack}
            saving={false}
          />
        )}
      </OnboardingLayout>
    </div>
  );
}
